/**
 * Phase 3 (unified entry) — cross-audience gentle-redirect surface.
 *
 * Displays a short copy block explaining we're routing the visitor to the
 * other audience's entry point, then auto-redirects after 2 seconds. The
 * target URL preserves the email as a query-string so the destination
 * lookup can auto-continue (email pre-filled on /apply or /employer-signin).
 *
 * Rendered as a full panel inside the existing `.team-track` / `.lg-section`
 * chrome — no new CSS required.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import BackLink from "./BackLink";
import { track } from "../lib/analytics";

const COUNTDOWN_SECONDS = 2;

export default function WrongDoorRedirect({
    to,
    email,
    direction,   // "toEmployee" | "toEmployer"
    onCancel,
    testId = "wrong-door-redirect",
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [left, setLeft] = useState(COUNTDOWN_SECONDS);

    useEffect(() => {
        track("wrong_door_shown", { direction, to });
        const tick = setInterval(() => setLeft((v) => v - 1), 1000);
        const done = setTimeout(() => {
            const target = email
                ? `${to}?email=${encodeURIComponent(email)}`
                : to;
            track("wrong_door_redirected", { direction, to });
            navigate(target, { replace: true });
        }, COUNTDOWN_SECONDS * 1000);
        return () => {
            clearInterval(tick);
            clearTimeout(done);
        };
    }, [direction, email, navigate, to]);

    return (
        <section className="lg-section team-track" data-testid={testId}>
            <div className="team-panel">
                {onCancel && (
                    <BackLink
                        onClick={onCancel}
                        testId={`${testId}-back`}
                        label={t("auth.common.startOver")}
                    />
                )}
                <div className="section-label mono">
                    {t("auth.common.wrongDoor.label")}
                </div>
                <h1>{t(`auth.common.wrongDoor.${direction}`)}</h1>
                <p className="team-sub" data-testid={`${testId}-countdown`}>
                    {t("auth.common.wrongDoor.continuing", { seconds: Math.max(0, left) })}
                </p>
            </div>
        </section>
    );
}
